import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  Title,
  useGetList,
  useNotify,
  useStore,
} from "react-admin";
import dataProvider from "server/dataProvider";
import { graduationRequests } from "api/graduationApi";
import { useLocation } from "react-router";
import BackButtonToolbar from "features/BackButtonToolbar";
import { Box, Card, Typography } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import EditIcon from "@mui/icons-material/Edit";
import RemoveStudentDialog from "./RemoveStudentDialog";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useWatch } from "react-hook-form";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { formatStudentOption } from "components/Graduation/utils";

const STORE_KEY = "internshipIndividualSchedule.filters";

const WatchedAutocomplete = ({ intervalId, planId, onSelectionChange, selections }) => {
  const studentEducationId = useWatch({ name: "studentEducationId" });

  useEffect(() => {
    if (studentEducationId) {
      onSelectionChange(intervalId, studentEducationId);
    } else {
      onSelectionChange(intervalId, "");
    }
  }, [studentEducationId]);

  const excludeIds = Object.values(selections).filter(Boolean);

  return (
    <ReferenceInput
      source="studentEducationId"
      reference={graduationRequests.getInternshipStudentsSelect()}
      filter={{ planId, excludeIds }}
    >
      <SelectInput
        label="Студент"
        optionText={formatStudentOption(true)}
        style={{ width: "300px" }}
        emptyText={false}
      />
    </ReferenceInput>
  );
};

const UpdateStudents = () => {
  const notify = useNotify();
  const [filters] = useStore(STORE_KEY, null);
  const planId = filters?.planId;

  const location = useLocation();
  const record = location?.state?.record;

  const [studentsInfo, setStudentsInfo] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selections, setSelections] = useState({});

  const fetchStudents = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        graduationRequests.getIndividualSlotStudents(record?.id),
        null,
        "GET",
      );
      setStudentsInfo(data);
    } catch (err) {
      console.error(err);
    }
  }, [record?.id]);
  useEffect(() => {
    if (record?.id) {
      fetchStudents();
    }
  }, [record?.id, fetchStudents]);

  const handleSelectionChange = (intervalId, studentEducationId) => {
    setSelections(prev => ({
      ...prev,
      [intervalId]: studentEducationId,
    }));
  };

  const handleSubmit = async () => {
    const assignments = Object.entries(selections)
      .filter(([, studentEducationId]) => !!studentEducationId)
      .map(([intervalId, studentEducationId]) => ({
        slotIntervalId: Number(intervalId),
        studentEducationId,
      }));

    const payload = { assignments };

    try {
      await dataProvider.customRequest(
        graduationRequests.assignIndividualSlotIntervalStudents(),
        payload,
        "PUT",
      );
      fetchStudents();
      setIsEditMode(false);
      notify("Успешно обновяване на студент към часовия интервал", { type: "success" });
    } catch (err) {
      notify(err.message || "Грешка при обновяване на студент", { type: "error" });
    }
  };

  const { data: disciplines } = useGetList(administrationRequests.getDisciplinesSelect(), { filter: { ids: [record?.disciplineId] } });
  const disciplineName = disciplines?.find(item => item.id === record?.disciplineId)?.name;

  const { data: rooms } = useGetList(administrationRequests.getRoomsSelect(), { filter: { ids: [record?.roomId] } });
  const roomName = rooms?.find(item => item.id === record?.roomId)?.roomName;

  const { data: dateTypes } = useGetList(graduationRequests.getScheduleDateTypeSelect(), {});

  const { data: students } = useGetList(graduationRequests.getInternshipStudentsSelect(), { filter: { planId } });

  return (
    <Box sx={{ width: "100%", overflow: "hidden", pb: 3 }}>
      <Title title={`Индивидуално разписание ( ${disciplineName} )`} />
      <BackButtonToolbar />
      <Box
        sx={{
          display: "flex",
          alignItems: "end",
          justifyContent: "space-between",
          my: 2,
        }}
      >
        <Typography
          color={"primary"}
          sx={{ textAlign: "left" }}
        >
          Зала: <span style={{ fontWeight: "bold" }}>{`${roomName} (бокс номер: ${record?.boxNumber})`}</span>
        </Typography>
        {!isEditMode && (
          <SecuredComponent permission={permissions.canManageInternshipSchedulesStudentsAssignments}>
            <Button
              startIcon={<EditIcon />}
              label={"Промени студенти"}
              variant="contained"
              onClick={() => setIsEditMode(true)}
            />
          </SecuredComponent>
        )}
      </Box>

      <Card sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {record?.timeIntervals?.map(interval => {
          const dateTypeName = dateTypes?.find(dt => dt.id === interval.dateType)?.nameBg;
          const studentInfo = studentsInfo?.find(info => info.intervalId === interval.id);
          const hasStudent = studentInfo?.studentEducationId != null;
          const student = students?.find(s => s.id === studentInfo?.studentEducationId);

          return (
            <Card
              key={interval.id}
              sx={{ display: "flex", alignItems: "center", gap: 4, p: 2 }}
            >
              <Box>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                >
                  {dateTypeName}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                >
                  {interval.from} - {interval.to}
                </Typography>
              </Box>

              {isEditMode ? (
                <SimpleForm
                  toolbar={false}
                  defaultValues={{ studentEducationId: studentInfo?.studentEducationId }}
                >
                  <WatchedAutocomplete
                    intervalId={interval.id}
                    planId={planId}
                    onSelectionChange={handleSelectionChange}
                    selections={selections}
                  />
                </SimpleForm>
              ) : (
                <Box sx={{ flex: 1 }}>
                  {hasStudent ? (
                    <Typography>
                      {student?.fullName} ({student?.facultyNumber})
                    </Typography>
                  ) : (
                    <Typography color="success.main">Свободно</Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                {hasStudent && !isEditMode && (
                  <SecuredComponent permission={permissions.canManageInternshipSchedulesStudentsAssignments}>
                    <RemoveStudentDialog
                      intervalId={studentInfo?.intervalId}
                      studentEducationId={studentInfo?.studentEducationId}
                      fetchStudents={fetchStudents}
                    />
                  </SecuredComponent>
                )}
              </Box>
            </Card>
          );
        })}
        {isEditMode && (
          <Box sx={{ display: "flex", justifyContent: "space-between", paddingX: 2 }}>
            <Button
              label="Откажи"
              size="medium"
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              onClick={() => setIsEditMode(false)}
            />
            <Button
              label="Запази"
              size="medium"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default UpdateStudents;
